import {
  executePolling,
  getResponseCheckDefaultAction,
  REQUEST_STATUS_COMPLETED,
  REQUEST_STATUS_FAILED
} from '../../../../../share/util/api/polling';
import { executeSyncRequest } from '../../../../../share/util/api/syncRequest';
import apiService from '../../../../../share/util/api/service';
import logWrapper from '../../../../../share/logWrapper';
import WS45Dialog from '../Requirement045Dialog';
import handler from '../../../../../share/util/apiResultValidator';
import config from '../../../../../share/config';

export const METHODS = {
  DEFAULT_SESSION_PROCESS: "DefaultSessionProcess",
  READ_DRIVE_CYCLE_DATA: "ReadDriveCycleData",
  JUDGE_OBD_COMMUNICATION_STANDARD: "JudgeObdCommunicationStandard",
  READ_SYSTEM_NAME: "ReadSystemName",
  INIT_COMMUNICATION: "InitCommunication",
  GET_SUPPORT_MENU: "GetSupportMenu",
  JUDGE_READINESS_CODE: "JudgeReadinessCode",
  GET_READINESS_CODE_JUDGE_RESULT: "GetReadinessCodeJudgeResult",
  MEMORY_CLEAR: "MemoryClear",
  GET_MEMORY_CLEAR_RESULT: "GetMemoryClearResult",
  GET_ONBOARD_MONITOR_TEST: "GetOnboardMonitorTest",
  GET_DIAG_CODE: "GetDiagCode",
  GET_MONITOR_TEST_DATA: "GetMonitorTestData",
  GET_DIAG_MEASURED_DATA: "GetDiagMeasuredData",
  READ_DTC_TABLE_DATA_CONVERT: "ReadDtcTableDataConvert",
  READINESS_CODE_TABLE_DATA_CONVERT: "ReadinessCodeTableDataConvert",
  INIT_DATA_MONITOR: "InitDataMonitor",
  ONBOARD_SYSTEM_TEST: "ObdOnboardSystemTest",
  STANDBY_OBD_ONBOARD_MONITOR_TEST: "StandbyObdOnboardMonitorTest",
  MONITOR: "Monitor",
  GET_MEASURED_DATA: "GetMeasuredData",
  HANDLE_GET_MEASURED_DATA: "HandleGetMeasuredData",
  STOP_MONITOR: "StopMonitor",
  CLEAR_MEASURED_DATA: "ClearMeasuredData"
};

/**
 * additionalInfoから各workSupportIdで利用するDTC一覧を取得する
 * WS.45 と WS.125 で格納されている dtc リストが異なる。 そのため workSupportId で判定して取得するデータを切り替える
 * @param {Instance} self WS.45インスタンス
 * @returns {Array} 利用するDTC一覧
 */
export function getAllTargetDtcList(self) {
  return (self.workSupportId === 4501)
    ? self.additionalInfo.target_dtc_list
    : self.additionalInfo.error_judge_dtc_list;
}

/**
 * executePolling共通化関数
 * @param {instance} self 呼び出し元this
 * @param {string} service 対象サービス
 * @param {object} postRequestBody POST API実行時リクエストボディ
 * @param {string} postEndPoint POSTエンドポイント
 * @param {string} getEndPoint GETエンドポイント
 * @param {boolean} addOperationScreen operationScreenを付加するかどうか
 * @param {object?} setPollingTimeOutCallback ポーリング時のタイムアウト停止コールバック関数\
 *                                            停止時は引数 true、再開時は引数 false で呼ぶ
 * @param {number?} pollingRetryCount ポーリングのリトライ回数指定（指定なしの場合は、デフォルト値を使用）
 * @param {number?} pollingRetryInterval ポーリングのリトライ待機時間指定（指定なしの場合は、デフォルト値を使用）
 * @returns API実行結果
 */
export async function doPolling(
  self,
  service,
  postRequestBody,
  postEndPoint,
  getEndPoint,
  addOperationScreen = true,
  setPollingTimeOutCallback = () => { },
  pollingRetryCount = null,
  pollingRetryInterval = null
) {
  return await executePolling(
    self,
    // 呼び出したい POST API を実装
    async () => {
      return addOperationScreen
        ? await apiService.postCall(service, postEndPoint, postRequestBody, self.operationScreen)
        : await apiService.postCall(service, postEndPoint, postRequestBody);
    },
    // 呼び出したい GET API を実装
    async (postApiResponse) => {
      const endPoint = getEndPoint + `${postApiResponse?.data?.request_id}`;
      return addOperationScreen
        ? await apiService.getCall(service, endPoint, self.operationScreen)
        : await apiService.getCall(service, endPoint);
    },
    // エラー発生のコールバック
    (errorType) => { errorType; self.showUnexpectedError(true); },
    // POST API レスポンスチェックのコールバックはデフォルト処理を使用
    undefined,
    // GET API レスポンスチェックのコールバックの処理を実装（"failed" が返ってきた場合に無視するため）
    (getApiResponse) => {
      if (getApiResponse?.data?.status === REQUEST_STATUS_FAILED) {
        logWrapper.log(
          "receive failed response. continue.",
          getApiResponse?.data,
          true
        );
        return REQUEST_STATUS_COMPLETED;
      }
      return getResponseCheckDefaultAction(getApiResponse);
    },
    // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
    (result) => { self.validateErrorResult(result); },
    // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
    (result) => { self.validateErrorResult(result); },
    // ポーリング時のタイムアウト停止コールバック関数
    setPollingTimeOutCallback,
    // ポーリング回数/間隔については各API呼び出し側で設定、未指定の場合はデフォルト設定を使用
    pollingRetryCount,
    pollingRetryInterval
  );
}

/**
 * executeSyncRequest共通化関数
 * @param {instance} self 呼び出し元this
 * @param {string} service 対象サービス
 * @param {string} endPoint GETエンドポイント
 * @param {boolean} addOperationScreen operationScreenを付加するかどうか
 * @returns API実行結果
 */
export async function doSyncRequest(self, service, endPoint, addOperationScreen = true) {
  return await executeSyncRequest(
    self,
    async () => {
      return addOperationScreen
        ? await apiService.getCall(service, endPoint, self.operationScreen)
        : await apiService.getCall(service, endPoint);
    },
    // エラー発生のコールバック
    (errorType) => { errorType; return []; },
    // GET API で正常レスポンスが返ってきた際の内容チェックはデフォルト処理を利用
    undefined,
    // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する
    (result) => { self.validateErrorResult(result); },
  );
}

/**
 * 例外発生時処理
 * @param {string} exceptedMethodName 呼び出し元メソッド名
 * @param {instance} error エラーオブジェクト
 * @param {instance} self 呼び出し元this
 * @param {boolean} loadingOff trueならスピナーをOFFに変更する
 */
export async function exceptionHandler(exceptedMethodName, error, self, loadingOff = true) {
  console.error(`${exceptedMethodName} Error:`, error);
  const targetProcess = getTargetProcess(exceptedMethodName);
  switch (targetProcess) {
    case 0:
      // 共通エラーに遷移
      self.showUnexpectedError(loadingOff);
      break;
    case 1:
      // 通信エラーに遷移
      self.showCommunicationError(loadingOff);
      break;
    case 2:
      // エバポリークテスト失敗表示に遷移
      self.dialog.phase = WS45Dialog.PHASE.EVAPO_LEAK_TEST_FAILURE;
      break;
    case 3:
      // 計測データ削除へ遷移
      if (await self.clearMeasuredData(true, true)) {
        // 通信エラー表示へ遷移
        await self.showCommunicationError(true);
      }
      break;
    case 4:
      // デフォルトセッション遷移に遷移
      if (await self.defaultSessionProcess(0, 1, true, true)) {
        // 共通エラーへ遷移
        self.showUnexpectedError(true);
      }
      break;
    case 5:
      // モニタ停止処理へ遷移
      if (await self.stopMonitor(true, true)) {
        // 通信エラーへ遷移
        self.showCommunicationError(true);
      }
      break;
    case 6: {
      // データモニタ停止処理へ遷移
      const stopMonitorResult = await self.stopMonitor(false, false);
      // 計測データ削除へ遷移
      const clearMeasuredDataResult = await self.clearMeasuredData(false, false);
      if (stopMonitorResult && clearMeasuredDataResult) {
        // すべて正常終了の場合のみ通信エラー表示へ遷移
        await self.showCommunicationError(true);
      } else {
        self.validateErrorResult(self.errorResult);
      }
      break;
    }
  }
}

/**
 * 次に呼び出す処理番号を返す
 * @param {string} exceptedMethodName 呼び出し元メソッド名
 */
function getTargetProcess(exceptedMethodName) {
  const targetMap = {
    [METHODS.DEFAULT_SESSION_PROCESS]: 0,
    [METHODS.READ_DRIVE_CYCLE_DATA]: 1,
    [METHODS.JUDGE_OBD_COMMUNICATION_STANDARD]: 2,
    [METHODS.READ_SYSTEM_NAME]: 2,
    [METHODS.INIT_COMMUNICATION]: 2,
    [METHODS.GET_SUPPORT_MENU]: 2,
    [METHODS.JUDGE_READINESS_CODE]: 1,
    [METHODS.GET_READINESS_CODE_JUDGE_RESULT]: 1,
    [METHODS.MEMORY_CLEAR]: 1,
    [METHODS.GET_MEMORY_CLEAR_RESULT]: 1,
    [METHODS.GET_ONBOARD_MONITOR_TEST]: 3,
    [METHODS.GET_DIAG_CODE]: 3,
    [METHODS.GET_MONITOR_TEST_DATA]: 1,
    [METHODS.GET_DIAG_MEASURED_DATA]: 1,
    [METHODS.READ_DTC_TABLE_DATA_CONVERT]: 0,
    [METHODS.READINESS_CODE_TABLE_DATA_CONVERT]: 0,
    [METHODS.INIT_DATA_MONITOR]: 4,
    [METHODS.ONBOARD_SYSTEM_TEST]: 1,
    [METHODS.STANDBY_OBD_ONBOARD_MONITOR_TEST]: 1,
    [METHODS.MONITOR]: 5,
    [METHODS.GET_MEASURED_DATA]: 6,
    [METHODS.HANDLE_GET_MEASURED_DATA]: 6,
    [METHODS.STOP_MONITOR]: 1,
    [METHODS.CLEAR_MEASURED_DATA]: 0,
  };
  return targetMap[exceptedMethodName];
}

/**
 * finally処理
 * @param {instance} self 呼び出し元this
 * @param {boolean} loadingOff trueならスピナーをOFFに変更する
 */
export function terminationHandler(self, loadingOff = true) {
  self.$nextTick(() => {
    self.isLoading = loadingOff ? false : self.isLoading;
  });
}

/**
 * MID-TIDペアを取得するユーティリティ関数
 * @param {boolean} withPrefix - MID/TIDに "$" プレフィックスを付けるかどうか
 * @returns {Array} MID-TIDペアの配列
 */
export function getMidTidPairs(withPrefix = false) {
  const pairs = [
    ["3C", "C1"], ["3C", "C2"], ["3C", "C3"], ["3C", "C4"], ["3C", "C5"],
    ["3C", "C6"], ["3C", "C7"], ["3C", "C8"], ["3C", "C9"], ["3C", "CA"],
    ["3C", "CB"], ["3C", "E4"], ["3C", "D1"], ["3C", "D2"], ["3C", "D3"],
    ["3C", "D4"], ["3C", "D5"], ["3C", "D6"], ["3C", "D7"], ["3C", "D8"],
    ["3C", "D9"], ["3C", "DA"], ["3C", "DB"], ["3C", "DC"], ["3C", "DD"],
    ["3C", "DE"], ["3D", "EC"]
  ];

  if (withPrefix) {
    return pairs.map(([mid, tid]) => [`$${mid}`, `$${tid}`]);
  }
  return pairs;
}

/**
 * (WS.45) マーカーを登録する
 * @param {Instance} root 呼び出し元インスタンス
 * @param {String} measuresId 計測ID
 * @param {Object} ws45MarkerInfo WS.45,WS.125,データ再生のマーカー情報
 * @param {Number} elapsedTime 設置するマーカーの時間(更新および削除時はnullとする)
 * @param {Number} markerTime マーカーUUIDを数値化したもの
 * @param {String} comment マーカーのコメント
 * @param {Boolean} isShared 他ユーザーへの共有設定
 * @param {String} userId マーカーを登録するユーザーのID
 * @param {String} operationScreen リクエストヘッダscreen-nameに追加する画面名
 * @param {String} projectDataSource プロジェクトのデータソース情報（データ再生時に必要）
 * @returns {Object} マーカー情報
 */
export async function updateGraphMarker(
  root, measuresId, ws45MarkerInfo, elapsedTime, markerTime, comment, isShared, userId, operationScreen, projectDataSource
) {
  const endPoint = '/projects/' + ws45MarkerInfo.projectId + '/' + measuresId;
  const timestamp = elapsedTime === null ? markerTime : elapsedTime + ws45MarkerInfo.baseTime;

  const body = {
    update_type: 'comment',
    monitor_comment: {
      timestamp: String(timestamp),
      comment: comment,
      is_shared: isShared,
      user_id: userId
    }
  };

  if (root.operationScreen === config.EXECUE_FUNCTION_TYPE_DATAPLAYBACK) body.region_info = projectDataSource;
  // マーカー登録のAPIとして呼び出す
  const response = await apiService.postCall(apiService.WSP, endPoint, body, operationScreen);

  let ret = null;
  // APIレスポンスの検証
  // レスポンス不正時はマーカのエラーダイアログを表示するため isAlert は false にする
  handler.validate(handler.validateTypes.all, response, root, null, () => {
    // 正常応答コールバック
    ret = {
      uuid: String(timestamp),
      detail: {
        occurred_elapsed_time: (timestamp - ws45MarkerInfo.baseTime) * 1000
      },
      description: comment,
      tag: { userId: userId, shared: String(isShared) }
    };
  }, (result) => {
    // レスポンス不正時のコールバック
    // エラー内容に関わらず error を返す
    ret = {
      error: "MarkerError",
    };
    logWrapper.log(
      "receive invalid response. continue.",
      result,
      true
    );
  }, null, false);

  return ret;
}

/**
 * (WS.45) マーカーを取得する
 * @param {String} root
 * @param {String} measuresId
 * @param {String} ws45MarkerInfo マーカ情報
 * @param {String} timestamp 取得したいマーカーの時間
 * @param {String} projectDataSource プロジェクトのデータソース情報（データ再生時に必要）
 * @returns {Object} マーカー情報
 */
export async function getGraphMarker(root, measuresId, ws45MarkerInfo, timestamp, projectDataSource) {
  const endPoint = '/workSupports/' + ws45MarkerInfo.projectId + '/' + measuresId;
  const param = { commentStamp: timestamp };
  if (root.operationScreen === config.EXECUE_FUNCTION_TYPE_DATAPLAYBACK) param.region_info = projectDataSource;
  const response = await apiService.getParamCall(apiService.WSP, endPoint, param);

  let ret = null;
  // レスポンス不正時はマーカのエラーダイアログを表示するため isAlert は false にする
  handler.validate(handler.validateTypes.all, response, root, null, async () => {
    const commentData = response.data.project.comment;
    // value が有効で、空オブジェクトではない場合の処理（コメント情報が存在しない場合 {} で戻ってくるのでチェック）
    if (commentData && !(Object.keys(commentData).length === 0)) {
      ret = {
        uuid: timestamp,
        detail: {
          occurred_elapsed_time: (commentData.time - ws45MarkerInfo.baseTime) * 1000
        },
        description: commentData.comment,
        tag: { userId: commentData.user_id, shared: String(commentData.is_shared) }
      };
    } else {
      ret = {
        error: "MarkerError",
      };
      logWrapper.log(
        "receive invalid comment. continue.",
        commentData,
        true
      );
    }
  }, (result) => {
    // レスポンス不正時のコールバック
    // エラー内容に関わらず error を返す
    ret = {
      error: "MarkerError",
    };
    logWrapper.log(
      "receive invalid response. continue.",
      result,
      true
    );
  }, null, false);

  return ret;
}

export default {
  METHODS,
  getAllTargetDtcList,
  doPolling,
  doSyncRequest,
  exceptionHandler,
  terminationHandler,
  getMidTidPairs,
  updateGraphMarker,
  getGraphMarker
};